import BeeswaxCatchupBehavior from '@/Enums/Beeswax/BeeswaxCatchupBehavior.enum';
import BeeswaxPacingBehavior from '@/Enums/Beeswax/BeeswaxPacingBehavior.enum';
import type { ICampaignManagementLineItem, ILineItemForm } from '@/Interfaces/CampaignManagement/LineItem.interface';
import AudienceTargetingService from '@/Services/AudienceTargeting.service';
import DealGroupsService from '@/Services/Deals/DealGroups.service';
import { LINE_ITEM_DEFAULT_GEO, LineItemChannels } from '@/constants/campaignManagement';
import { CHANNEL_ID_MAP } from '@/helpers/forecaster';
import moment from 'moment';
import { mapLineItemCreativesToFormStructure } from '../campaignManagement';
import { devicesToControlOptions } from '../devices';
import { firstPartyBeeswaxSegmentsToControlOptions } from './firstPartySegments';

export const lineItemApiDataToFormStructure = (lineItem: ICampaignManagementLineItem): ILineItemForm => {
  const {
    id,
    isRibeyeEntry,
    name,
    impressions_budget: impressionsBooked,
    channel,
    devices,
    sold_cpm: soldCpm,
    geo,
    geo_fence: geoFence,
    product,
    audienceMeta,
    firstPartySegmentsMeta,
    daypart,
    frequency_caps: frequencyCaps,
    pacing_behavior: pacingBehavior,
    catchup_behavior: catchupBehavior,
    impressions_budget_daily: dailyBudget,
    start_date: startDate,
    end_date: endDate,
    creatives,
    bid_cpm: bidCpm,
    publishers,
    dealGroups,
    package_id: packageId,
    alt_id: altId,
    pacing,
    has_custom_domain_lists: hasCustomDomainLists,
  } = lineItem;

  const publisherIds = publishers?.map(({ id }) => id) || [];

  return {
    id,
    isRibeyeEntry,
    name,
    impressionsBooked: String(impressionsBooked),
    soldCpm: soldCpm ? String(soldCpm) : null,
    channel: channel ? { label: channel.name, value: channel.id } : null,
    devices: devicesToControlOptions(devices ?? []),
    product,
    frequencyCaps: frequencyCaps
      ? {
          daily: frequencyCaps.daily ? String(frequencyCaps.daily) : null,
          weekly: frequencyCaps.weekly ? String(frequencyCaps.weekly) : null,
          hourly: frequencyCaps.hourly ? String(frequencyCaps.hourly) : null,
          monthly: frequencyCaps.monthly ? String(frequencyCaps.monthly) : null,
        }
      : {},
    pacingBehavior: pacingBehavior ?? BeeswaxPacingBehavior.Even,
    catchupBehavior: catchupBehavior ?? BeeswaxCatchupBehavior.Even,
    dailyBudget: dailyBudget ? String(dailyBudget) : null,
    duplicates: [],
    creatives: mapLineItemCreativesToFormStructure(creatives),
    geo: geo ?? LINE_ITEM_DEFAULT_GEO,
    geoFence: geoFence ?? null,
    audiences: LineItemChannels.reduce((acc, ch) => {
      const channelId = CHANNEL_ID_MAP[ch];
      acc[channelId] = AudienceTargetingService.audienceMetaTargetingToAudienceControlOptionTargeting(
        audienceMeta?.[channelId] ?? audienceMeta?.[ch],
      );
      return acc;
    }, AudienceTargetingService.defaultLineItemAudienceTargeting),
    firstPartySegments: firstPartyBeeswaxSegmentsToControlOptions(firstPartySegmentsMeta) || [],
    daypart,
    startDate: moment.utc(startDate),
    endDate: moment.utc(endDate),
    bidCpm: bidCpm ? String(+bidCpm) : null,
    publisherIds,
    dealGroups: DealGroupsService.dealGroupsToControlOptions(dealGroups),
    packageId: packageId ?? null,
    altId: altId ?? '',
    pacing,
    hasCustomDomainLists: hasCustomDomainLists ?? false,
  };
};
